import { Container, Graphics, Text } from 'pixi.js';

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';

export interface CrashChartOptions {
  width: number;
  height: number;
  /** Multiplier at which the chart's vertical scale tops out. Larger values
   * mean a flatter early curve. Default 5×. */
  maxScaleMultiplier?: number;
  /** Horizontal time window the chart covers, in ms. Default 12000. */
  timeWindowMs?: number;
  /** Curve stroke colour. Default brand pink. */
  curveColor?: number;
  /** Crash flash colour. Default red. */
  crashColor?: number;
  /** Cash-out flash colour. Default warm gold. */
  cashOutColor?: number;
}

const STATE_RISING = 0;
const STATE_CASHED = 1;
const STATE_CRASHED = 2;

/**
 * Live multiplier chart for the Crash mini-game. Y-axis is the multiplier
 * (auto-scales as the curve climbs past {@link CrashChartOptions#maxScaleMultiplier});
 * X-axis is elapsed round time.
 *
 * <p>The big {@code 1.23×} number sits centred on top of the chart;
 * subscribers feed in points via {@link addPoint}. {@link reset} clears
 * the chart for a new round; {@link flashState} repaints the curve in the
 * cashed-out / crashed colour for the post-round freeze frame.
 */
export class CrashChart extends Container {
  private readonly bg: Graphics;
  private readonly curve: Graphics;
  private readonly axis: Graphics;
  private readonly multiplierText: Text;
  private readonly w: number;
  private readonly h: number;
  private readonly maxScale: number;
  private readonly timeWindowMs: number;
  private readonly curveColor: number;
  private readonly crashColor: number;
  private readonly cashOutColor: number;
  private points: { t: number; m: number }[] = [];
  private state = STATE_RISING;
  private currentMaxScale: number;

  constructor(options: CrashChartOptions) {
    super();
    this.w = options.width;
    this.h = options.height;
    this.maxScale = options.maxScaleMultiplier ?? 5;
    this.currentMaxScale = this.maxScale;
    this.timeWindowMs = options.timeWindowMs ?? 12000;
    this.curveColor = options.curveColor ?? 0xff6bd6;
    this.crashColor = options.crashColor ?? 0xff3b6b;
    this.cashOutColor = options.cashOutColor ?? 0xfff59d;

    this.bg = new Graphics();
    this.bg
      .roundRect(-this.w / 2, -this.h / 2, this.w, this.h, 14)
      .fill({ color: 0x1a0830, alpha: 0.85 })
      .stroke({ color: this.curveColor, alpha: 0.3, width: 2 });
    this.addChild(this.bg);

    this.axis = new Graphics();
    this.addChild(this.axis);

    this.curve = new Graphics();
    this.addChild(this.curve);

    this.multiplierText = new Text({
      text: '1.00×',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: Math.floor(this.h * 0.32),
        fontWeight: '900',
        fill: 0xffffff,
        align: 'center',
      },
    });
    this.multiplierText.anchor.set(0.5);
    this.multiplierText.y = -this.h * 0.12;
    this.addChild(this.multiplierText);

    this.drawAxis();
  }

  /** Clear the chart and seed it at (t=0, m=1) for a new round. */
  reset(): void {
    this.points = [{ t: 0, m: 1 }];
    this.state = STATE_RISING;
    this.currentMaxScale = this.maxScale;
    this.multiplierText.text = '1.00×';
    this.multiplierText.style.fill = 0xffffff;
    this.curve.clear();
  }

  /** Append a point and redraw the curve. */
  addPoint(elapsedMs: number, multiplier: number): void {
    this.points.push({ t: elapsedMs, m: multiplier });
    if (multiplier > this.currentMaxScale) {
      // Auto-scale: bump the y-axis ceiling so the rising curve doesn't
      // pin to the top edge. Step in 25% jumps so the redraw isn't jittery.
      this.currentMaxScale = Math.ceil(multiplier * 1.25 * 4) / 4;
    }
    this.multiplierText.text = `${multiplier.toFixed(2)}×`;
    this.redrawCurve();
  }

  /** Repaint the curve in the cashed-out colour and update the centre text. */
  flashCashedOut(multiplier: number): void {
    this.state = STATE_CASHED;
    this.multiplierText.text = `+${this.formatWinningsLine(multiplier)}`;
    this.multiplierText.style.fill = this.cashOutColor;
    this.redrawCurve();
  }

  /** Repaint the curve in the crashed colour and update the centre text. */
  flashCrashed(crashedAt: number): void {
    this.state = STATE_CRASHED;
    this.multiplierText.text = `💥 ${crashedAt.toFixed(2)}×`;
    this.multiplierText.style.fill = this.crashColor;
    this.redrawCurve();
  }

  private formatWinningsLine(multiplier: number): string {
    return `${multiplier.toFixed(2)}× CASHED`;
  }

  private drawAxis(): void {
    this.axis.clear();
    // Faint horizontal grid lines at 1×, 2×, 5×, 10× when within range.
    const stops = [1, 2, 5, 10, 25, 50, 100];
    for (const m of stops) {
      if (m > this.currentMaxScale) break;
      const y = this.yForMultiplier(m);
      this.axis.moveTo(-this.w / 2 + 8, y);
      this.axis.lineTo(this.w / 2 - 8, y);
    }
    this.axis.stroke({ color: 0xffffff, alpha: 0.06, width: 1 });
  }

  private redrawCurve(): void {
    this.drawAxis();
    this.curve.clear();
    if (this.points.length < 2) return;
    const colour = this.colourForState();
    const first = this.points[0]!;
    this.curve.moveTo(this.xForT(first.t), this.yForMultiplier(first.m));
    for (let i = 1; i < this.points.length; i++) {
      const p = this.points[i]!;
      this.curve.lineTo(this.xForT(p.t), this.yForMultiplier(p.m));
    }
    this.curve.stroke({ color: colour, alpha: 0.95, width: 4, cap: 'round', join: 'round' });
  }

  private colourForState(): number {
    if (this.state === STATE_CASHED) return this.cashOutColor;
    if (this.state === STATE_CRASHED) return this.crashColor;
    return this.curveColor;
  }

  private xForT(t: number): number {
    const clamped = Math.max(0, Math.min(this.timeWindowMs, t));
    return -this.w / 2 + 8 + (clamped / this.timeWindowMs) * (this.w - 16);
  }

  private yForMultiplier(m: number): number {
    const clamped = Math.max(1, Math.min(this.currentMaxScale, m));
    // 1× sits at the bottom; max-scale sits at the top.
    const t = (clamped - 1) / (this.currentMaxScale - 1);
    return this.h / 2 - 12 - t * (this.h - 24);
  }
}
